import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Persisted view-mode preference for the Proposals list (CLAUDE.md §2.3).
 *
 * Search / status filter live in the page state and stay out of the store —
 * those are server-driven query params, not durable user prefs.
 */
export type ProposalsViewMode = 'table' | 'list' | 'cards' | 'grouped';

export const PROPOSALS_VIEW_MODES: ProposalsViewMode[] = [
  'table',
  'list',
  'cards',
  'grouped',
];

interface ProposalsUiState {
  view: ProposalsViewMode;
  setView: (view: ProposalsViewMode) => void;
  /** Transient toggle — refreshes default to the active list. */
  showArchived: boolean;
  setShowArchived: (showArchived: boolean) => void;
  /** Current DRF `OrderingFilter` value (e.g. `-created`). Session-only. */
  ordering: string | undefined;
  setOrdering: (ordering: string | undefined) => void;
  /** Persisted filters — survive reloads. */
  status: string;
  setStatus: (status: string) => void;
  /** Owner (salesperson) filter — a User id as a string; '' = all. */
  owner: string;
  setOwner: (owner: string) => void;
  createdFrom: string;
  setCreatedFrom: (v: string) => void;
  createdTo: string;
  setCreatedTo: (v: string) => void;
}

export const useProposalsUi = create<ProposalsUiState>()(
  persist(
    (set) => ({
      view: 'table',
      setView: (view) => set({ view }),
      showArchived: false,
      setShowArchived: (showArchived) => set({ showArchived }),
      ordering: undefined,
      setOrdering: (ordering) => set({ ordering }),
      status: 'all',
      setStatus: (status) => set({ status }),
      owner: '',
      setOwner: (owner) => set({ owner }),
      createdFrom: '',
      setCreatedFrom: (createdFrom) => set({ createdFrom }),
      createdTo: '',
      setCreatedTo: (createdTo) => set({ createdTo }),
    }),
    {
      name: 'crm.proposals:ui',
      partialize: (s) => ({
        view: s.view,
        status: s.status,
        owner: s.owner,
        createdFrom: s.createdFrom,
        createdTo: s.createdTo,
      }),
    },
  ),
);
